import React from 'react';
import { ImageSrcSet } from './Image';
import { cn } from '@express-labs/raven-tools';
import s from './Image.scss';

type RenderImageTagProps = {
  isError: boolean;
  isLoaded: boolean;
  isLoading: boolean;
  srcSet?: ImageSrcSet[];
  handleOnLoad?: (ev: React.SyntheticEvent<HTMLImageElement>) => void;
  handleOnError?: (ev: React.SyntheticEvent<HTMLImageElement>) => void;
  sortedSrcSet: ImageSrcSet[];
};

const RenderImageTag = React.forwardRef<
  HTMLImageElement,
  Omit<JSX.IntrinsicElements['img'], keyof RenderImageTagProps> &
    RenderImageTagProps
>(
  (
    {
      alt,
      className,
      handleOnError,
      handleOnLoad,
      isError,
      isLoaded,
      isLoading,
      sortedSrcSet,
      src,
      srcSet,
      ...restOfProps
    },
    ref
  ) => {
    const imageClassName = cn(
      s.picture,
      'raven-ui-image',
      isLoading && 'raven-ui-image--loading',
      isLoaded && 'raven-ui-image--loaded',
      isError && 'raven-ui-image--error',
      className
    );

    if (!srcSet?.length) {
      return (
        <img
          alt={alt}
          className={imageClassName}
          onError={handleOnError}
          onLoad={handleOnLoad}
          ref={ref}
          src={src}
          {...restOfProps}
        />
      );
    }

    const defaultSourceSet =
      sortedSrcSet[sortedSrcSet.length - 1].breakPoint + 1;

    return (
      <picture className={imageClassName} ref={ref}>
        {sortedSrcSet.map((source) => (
          <source
            key={`${source.src}_${source.breakPoint}`}
            media={`(max-width: ${source.breakPoint}px)`}
            srcSet={source.src}
          />
        ))}
        <source media={`(min-width: ${defaultSourceSet}px)`} srcSet={src} />
        <img
          alt={alt}
          onLoad={handleOnLoad}
          onError={handleOnError}
          src={src}
          {...restOfProps}
        />
      </picture>
    );
  }
);

RenderImageTag.displayName = 'RenderImageTag';

export default React.memo(RenderImageTag);
